import type { ReactElement } from 'react'

import type { SceneName } from '@maria-cavalheiro-website/shared-types'

import { PoemFigure } from './poem-figure.tsx'
import { emblemParts, type PlacedFigure } from './scene-art.ts'

export interface PoemPlateProps {
  readonly name: SceneName
}

/**
 * Index into the figure list, and nothing more.
 *
 * Every other key would be a lie dressed as an identifier: the figures carry no
 * id, several are byte-identical to a sibling (the second dove in *Pombas no
 * telhado*, the second bird in *Andorinhas*), and the lists are authored, frozen
 * and never reordered, inserted into or filtered. Position IS the identity here.
 */
function plateFigure(figure: PlacedFigure, index: number): ReactElement {
  return (
    <PoemFigure
      key={index}
      figure={figure}
      className={`cena__parte cena__parte--${figure.placement}`}
      // The plate sits above the title, so its lead is the one image on the page
      // that can plausibly win LCP. Fetching it eagerly is what makes that
      // outcome cheap rather than something to avoid.
      priority={index === 0}
    />
  )
}

/**
 * The poem's title plate: its two or three figures, composed.
 *
 * IN THE FLOW, WHICH IS THE WHOLE OF PHASE 0018's CHANGE HERE. This was a
 * `position: fixed; inset: 0` layer at `z-index: 0` — a picture pinned to the
 * viewport that the poem scrolled past, at 0.88 opacity beside the column above
 * 72em and at 0.11 in a phone's corner below it. It is an ordinary block now,
 * above the title, at full strength, the same on every viewport.
 *
 * NOTHING IS PARAMETERISED BUT THE NAME. The composition is five authored
 * placements shared by all twenty-three emblems, and each figure carries which
 * one it takes. The geometry behind those five names is in `_scenes.sass` and has
 * to be: `style-src 'self'` admits no inline `style` attribute, so a per-figure
 * inset cannot be written onto the element even if we wanted it there.
 *
 * NO `aria-hidden` ANYWHERE. Each figure is an `<img alt="">`, which is already
 * out of the accessibility tree; a wrapper repeating it would be stating the same
 * fact twice in a weaker way. The old inline `<svg>` needed both that and
 * `focusable="false"`, because legacy engines made SVG focusable by default —
 * an `<img>` has never had that problem.
 */
export function PoemPlate({ name }: PoemPlateProps): ReactElement {
  return <div className="cena">{emblemParts(name).map(plateFigure)}</div>
}
